import { useForm } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import { Alert, Button, Card, Checkbox, Divider, Form, Input, InputNumber, Radio, Select, Space, Switch, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useGo, useList, useInvalidate, useOne } from "@refinedev/core";
import { useParams } from "react-router-dom";

const { Text } = Typography;
const { TextArea } = Input;

// API URL из env
const API_URL = (import.meta as any).env?.VITE_API_URL || "http://localhost:8085";

// Интерфейс категории из справочника
interface StopWordCategory {
  id: string;
  label: string;
  color: string;
  action_type: "block" | "trigger";
  description?: string;
}

type ConditionOperator = "equals" | "not_equals" | ">" | "<" | ">=" | "<=" | "contains" | "in" | "is";

interface Condition {
  field: string;
  operator: ConditionOperator;
  value: any;
}

interface RoutingRule {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  is_fallback: boolean;
  priority: number;
  auto_publish?: boolean;
  conditions: {
    operator: string;
    conditions: Condition[];
  };
  generation_config: {
    mode: "ai" | "template" | "manual";
    [key: string]: any;
  };
  brands: string[];
  sources: string[];
}

export default function RoutingRuleEdit() {
  const go = useGo();
  const invalidate = useInvalidate();
  const { formProps, formLoading, form } = useForm<RoutingRule>({
    resource: "strategies",
    redirect: false,
    mutationMode: "pessimistic",
    queryOptions: {
      gcTime: 0,
      staleTime: 0,
    },
    onMutationSuccess: () => {
      invalidate({ resource: "strategies", invalidates: ["resourceAll"] });
      go({
        to: "/strategies",
        type: "replace",
        options: { keepQuery: true },
      });
    },
    successNotification: () => ({
      type: "success",
      message: "Успешно",
      description: "Правило маршрутизации обновлено",
    }),
    errorNotification: (error: any) => {
      // Извлекаем понятное сообщение из разных форматов ошибок
      let description = "Проверьте обязательные поля и повторите попытку";

      // Пытаемся извлечь сообщение из разных мест
      const rawDetail = error?.response?.data?.detail;

      // Обработка ошибок валидации Pydantic (массив объектов с полем msg)
      if (Array.isArray(rawDetail) && rawDetail.length > 0) {
        description = rawDetail.map((e: any) => e.msg || JSON.stringify(e)).join('; ');
      } else if (rawDetail && typeof rawDetail === 'string') {
        description = rawDetail;
      } else if (error?.message) {
        description = error.message;
      } else if (error?.detail) {
        description = typeof error.detail === 'object'
          ? JSON.stringify(error.detail)
          : String(error.detail);
      } else if (typeof error === 'string') {
        description = error;
      }

      // Особая обработка для ошибки duplicate priority
      if (description.includes("уже существует") || description.includes("duplicate")) {
        return {
          type: "error",
          message: "Конфликт приоритета",
          description: description,
        };
      }

      return {
        type: "error",
        message: "Не удалось сохранить",
        description: description,
      };
    },
  });

  const [actionType, setActionType] = useState<"ai" | "template" | "manual">("ai");
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false); // Флаг для предотвращения повторной загрузки
  const [isFallback, setIsFallback] = useState(false); // Флаг fallback правила
  const [allCategories, setAllCategories] = useState<StopWordCategory[]>([]); // Категории из справочника

  // Загрузка категорий из справочника при монтировании
  useEffect(() => {
    fetch(`${API_URL}/api/v1/stop-word-categories`)
      .then(res => res.json())
      .then(data => {
        setAllCategories(data.items || []);
        console.log('✅ Загружено категорий из справочника:', (data.items || []).length);
      })
      .catch(err => console.error('❌ Ошибка загрузки категорий:', err));
  }, []);

  // Fetch active templates
  const templatesQuery = useList({
    resource: "templates",
    filters: [{ field: "is_active", operator: "eq", value: true }],
    pagination: { pageSize: 100 },
  });

  const templates = templatesQuery.result?.data || [];

  // Fetch active AI styles
  const aiStylesQuery = useList({
    resource: "ai-styles",
    filters: [{ field: "is_active", operator: "eq", value: true }],
  });

  const aiStyles = aiStylesQuery.result?.data || [];

  // Load existing data когда форма загрузилась (ТОЛЬКО ОДИН РАЗ!)
  useEffect(() => {
    if (!formLoading && form && !dataLoaded) {
      // Получаем все данные из formProps.initialValues
      const data = (formProps as any).initialValues;

      // Защита от undefined data
      if (!data) {
        console.log('🔄 [FORM LOAD] initialValues is undefined, skipping');
        return;
      }

      console.log('🔄 [FORM LOAD] initialValues:', JSON.stringify(data, null, 2));

      // Load action type from generation_config.mode (с защитой от undefined)
      const mode = data?.generation_config?.mode;
      console.log('🔄 [FORM LOAD] mode из БД:', mode);

      if (mode) {
        console.log('🔄 [FORM LOAD] Установка actionType:', mode);
        setActionType(mode);

        // Если режим template - загрузить template_ids (массив)
        if (mode === 'template' && data.generation_config?.template_ids && Array.isArray(data.generation_config.template_ids)) {
          const templateIds = data.generation_config.template_ids;
          console.log('🔄 [FORM LOAD] Установка template_ids:', templateIds);
          if (templateIds.length > 0 && form) {
            // Используем setTimeout для надежности
            setTimeout(() => {
              form.setFieldValue('template_ids', templateIds);
              const checkValue = form.getFieldValue('template_ids');
              console.log('✅ [FORM LOAD] template_ids установлен:', checkValue);
            }, 200);
          }
        }

        // Если режим ai - загрузить style и use_rag
        if (mode === 'ai' && data.generation_config && form) {
          setTimeout(() => {
            // Загружаем style (название AI стиля)
            if (data.generation_config.style) {
              form.setFieldValue(['ai_config', 'style'], data.generation_config.style);
              console.log('✅ [FORM LOAD] style установлен:', data.generation_config.style);
            }
            // Важно: загружаем use_rag из БД (по умолчанию true если не указан)
            const useRag = data.generation_config.use_rag !== false;
            form.setFieldValue(['ai_config', 'use_rag'], useRag);
            console.log('✅ [FORM LOAD] use_rag установлен:', useRag);
          }, 200);
        }
      }

      // Load conditions
      if (data.conditions?.conditions && Array.isArray(data.conditions.conditions)) {
        setConditions(data.conditions.conditions);
      }

      // Загрузить флаг is_fallback
      if (data.is_fallback) {
        setIsFallback(true);
        console.log('🔄 [FORM LOAD] Fallback правило - приоритет заблокирован');
      }

      // Помечаем что данные загружены
      setDataLoaded(true);
      console.log('✅ [FORM LOAD] Данные загружены, флаг установлен');
    }
  }, [formLoading, form, dataLoaded]); // Убрали formProps из dependencies!

  const addCondition = () => {
    setConditions([...conditions, { field: "rating", operator: "equals", value: 5 }]);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    setConditions(newConditions);
  };

  const fieldOptions = [
    { value: "type", label: "Тип" },
    { value: "rating", label: "Рейтинг" },
    { value: "sentiment", label: "Тональность" },
    { value: "has_text", label: "Есть текст" },
    { value: "has_advantages", label: "Есть достоинства" },
    { value: "has_disadvantages", label: "Есть недостатки" },
    { value: "has_question_mark", label: "Есть вопрос" },
    { value: "has_stop_words", label: "Есть стоп-слова" },
    { value: "stop_word_categories", label: "Категория блокирующих слов" },
    { value: "trigger_word_categories", label: "Категория триггер-слов" },
    { value: "photo_count", label: "Количество фото" },
    { value: "source", label: "Источник" },
    { value: "brand", label: "Бренд" },
  ];

  // Категории из справочника - фильтруем по action_type
  const stopWordCategoryOptions = allCategories
    .filter(c => c.action_type === 'block')
    .map(c => ({ value: c.id, label: c.label }));

  const triggerWordCategoryOptions = allCategories
    .filter(c => c.action_type === 'trigger')
    .map(c => ({ value: c.id, label: c.label }));

  const operatorOptions: Record<string, { value: ConditionOperator; label: string }[]> = {
    type: [
      { value: "equals", label: "равно" },
      { value: "not_equals", label: "не равно" },
    ],
    rating: [
      { value: "equals", label: "=" },
      { value: ">", label: ">" },
      { value: "<", label: "<" },
      { value: ">=", label: ">=" },
      { value: "<=", label: "<=" },
    ],
    sentiment: [
      { value: "equals", label: "равно" },
      { value: "not_equals", label: "не равно" },
    ],
    has_text: [{ value: "is", label: "является" }],
    has_advantages: [{ value: "is", label: "является" }],
    has_disadvantages: [{ value: "is", label: "является" }],
    has_question_mark: [{ value: "is", label: "является" }],
    has_stop_words: [{ value: "is", label: "является" }],
    photo_count: [
      { value: "equals", label: "=" },
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
    ],
    source: [
      { value: "in", label: "входит в список" },
      { value: "equals", label: "равно" },
    ],
    brand: [
      { value: "in", label: "входит в список" },
      { value: "equals", label: "равно" },
    ],
    stop_word_categories: [
      { value: "contains", label: "содержит" },
    ],
    trigger_word_categories: [
      { value: "contains", label: "содержит" },
    ],
  };

  const renderValueInput = (condition: Condition, index: number) => {
    const field = condition.field;

    if (field === "type") {
      return (
        <Select
          style={{ width: 200 }}
          value={condition.value}
          onChange={(value) => updateCondition(index, { value })}
          options={[
            { value: "review", label: "Отзыв" },
            { value: "question", label: "Вопрос" },
          ]}
          data-testid={`condition-value-${index}`}
        />
      );
    }

    if (field === "sentiment") {
      return (
        <Select
          style={{ width: 200 }}
          value={condition.value}
          onChange={(value) => updateCondition(index, { value })}
          options={[
            { value: "positive", label: "Позитивный" },
            { value: "negative", label: "Негативный" },
            { value: "neutral", label: "Нейтральный" },
          ]}
          data-testid={`condition-value-${index}`}
        />
      );
    }

    if (field === "rating") {
      return (
        <InputNumber
          style={{ width: 100 }}
          min={1}
          max={5}
          value={condition.value}
          onChange={(value) => updateCondition(index, { value })}
          data-testid={`condition-value-${index}`}
        />
      );
    }

    if (["has_text", "has_advantages", "has_disadvantages", "has_question_mark", "has_stop_words"].includes(field)) {
      return (
        <Select
          style={{ width: 120 }}
          value={condition.value}
          onChange={(value) => updateCondition(index, { value })}
          options={[
            { value: true, label: "Да" },
            { value: false, label: "Нет" },
          ]}
          data-testid={`condition-value-${index}`}
        />
      );
    }

    if (field === "photo_count") {
      return (
        <InputNumber
          style={{ width: 100 }}
          min={0}
          value={condition.value}
          onChange={(value) => updateCondition(index, { value })}
          data-testid={`condition-value-${index}`}
        />
      );
    }

    if (field === "source" || field === "brand") {
      return (
        <Input
          style={{ width: 300 }}
          value={condition.value}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          placeholder='Например: wildberries_cab1 или "wildberries_cab1,wildberries_cab2"'
          data-testid={`condition-value-${index}`}
        />
      );
    }

    if (field === "stop_word_categories") {
      return (
        <Select
          style={{ width: 250 }}
          value={condition.value}
          onChange={(value) => updateCondition(index, { value })}
          options={stopWordCategoryOptions}
          placeholder="Выберите категорию"
          data-testid={`condition-value-${index}`}
        />
      );
    }

    if (field === "trigger_word_categories") {
      return (
        <Select
          style={{ width: 250 }}
          value={condition.value}
          onChange={(value) => updateCondition(index, { value })}
          options={triggerWordCategoryOptions}
          placeholder="Выберите категорию"
          data-testid={`condition-value-${index}`}
        />
      );
    }

    return (
      <Input
        style={{ width: 200 }}
        value={condition.value}
        onChange={(e) => updateCondition(index, { value: e.target.value })}
        data-testid={`condition-value-${index}`}
      />
    );
  };

  const handleFinish = (values: any) => {
    console.log('🔍 [ROUTING SAVE] Начало сохранения...');
    console.log('🔍 [ROUTING SAVE] actionType:', actionType);

    // КРИТИЧНО: Добавляем conditions из локального стейта
    values.conditions = {
      operator: "AND",
      conditions: conditions
    };

    // Устанавливаем пустые массивы для brands/sources (фильтрация через conditions)
    values.brands = [];
    values.sources = [];

    // Создаем generation_config на основе action_type
    if (!values.generation_config) {
      values.generation_config = {};
    }

    // Устанавливаем mode
    values.generation_config.mode = actionType;

    // Преобразуем данные в зависимости от режима
    if (actionType === 'template') {
      // Для template режима - template_ids уже массив из Select mode="multiple"
      if (values.template_ids && values.template_ids.length > 0) {
        console.log('🔍 [ROUTING SAVE] template_ids:', values.template_ids);
        values.generation_config.template_ids = values.template_ids;
        delete values.template_ids;
      }
    } else if (actionType === 'ai') {
      // Для AI режима переносим ai_config в generation_config
      if (values.ai_config) {
        values.generation_config.style = values.ai_config.style;
        values.generation_config.use_rag = values.ai_config.use_rag !== false;
        delete values.ai_config;
      }
    }

    // Удаляем action_type - он больше не нужен
    delete values.action_type;

    console.log('🔍 [ROUTING SAVE] Финальные values:', JSON.stringify(values, null, 2));

    // Вызываем стандартный обработчик Refine
    if (formProps.onFinish) {
      console.log('🔍 [ROUTING SAVE] Вызов formProps.onFinish...');
      formProps.onFinish(values);
    } else {
      console.log('❌ [ROUTING SAVE] formProps.onFinish не определен!');
    }
  };

  return (
    <Card title="Редактирование правила маршрутизации" loading={formLoading} bordered>
      <Form {...formProps} onFinish={handleFinish} layout="vertical" autoComplete="off">
        <Card title="Основная информация" size="small" style={{ marginBottom: 16 }}>
          <Form.Item
            label="Название правила"
            name="name"
            rules={[{ required: true, message: "Введите название правила" }]}
          >
            <Input placeholder="Например: Позитивные отзывы 4-5★" size="large" />
          </Form.Item>

          <Form.Item
            label="Описание"
            name="description"
          >
            <TextArea
              rows={2}
              placeholder="Краткое описание правила и когда оно применяется"
            />
          </Form.Item>

          <Space size={24}>
            <Form.Item
              label="Приоритет"
              name="priority"
              rules={[{ required: true, message: "Укажите приоритет" }]}
            >
              <InputNumber
                min={1}
                max={100}
                style={{ width: 100 }}
                data-testid="priority-input"
                disabled={isFallback}
              />
            </Form.Item>

            <div style={{ fontSize: 12, color: '#666', marginTop: 30 }}>
              {isFallback
                ? "Приоритет fallback правила не может быть изменен"
                : "1-100, чем выше - тем раньше проверяется"
              }
            </div>
          </Space>

          <Form.Item
            label="Статус"
            name="is_active"
            valuePropName="checked"
          >
            <Switch checkedChildren="Активно" unCheckedChildren="Неактивно" />
          </Form.Item>

          <Form.Item
            label="Автоматическая публикация"
            name="auto_publish"
            valuePropName="checked"
            tooltip="Автоматически публиковать ответы без модерации"
          >
            <Switch checkedChildren="Да" unCheckedChildren="Нет" />
          </Form.Item>
        </Card>


        <Card title="Условия применения (КОГДА)" size="small" style={{ marginBottom: 16 }}>
          <Alert
            message="Все условия должны выполниться одновременно (логика И)"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {conditions.map((condition, index) => (
              <Space key={index} align="start" style={{ width: '100%' }} data-testid={`condition-row-${index}`}>
                <Select
                  style={{ width: 200 }}
                  value={condition.field}
                  onChange={(value) => {
                    // При смене поля сбрасываем operator и value
                    const defaultOp = operatorOptions[value]?.[0]?.value || "equals";
                    const defaultValue = value === "rating" ? 5 : (value.startsWith("has_") ? true : "");
                    updateCondition(index, { field: value, operator: defaultOp, value: defaultValue });
                  }}
                  options={fieldOptions}
                  data-testid={`condition-field-${index}`}
                />

                <Select
                  style={{ width: 150 }}
                  value={condition.operator}
                  onChange={(value) => updateCondition(index, { operator: value })}
                  options={operatorOptions[condition.field] || [{ value: "equals", label: "=" }]}
                  data-testid={`condition-operator-${index}`}
                />

                {renderValueInput(condition, index)}

                {conditions.length > 1 && (
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={() => removeCondition(index)}
                    danger
                    type="text"
                    data-testid={`delete-condition-${index}`}
                  />
                )}
              </Space>
            ))}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addCondition}
              style={{ width: '100%' }}
              data-testid="add-condition-btn"
            >
              Добавить условие
            </Button>
          </Space>

          <Form.Item name="conditions" initialValue={{}} hidden>
            <Input />
          </Form.Item>
        </Card>

        <Card title="Действие (ЧТО ДЕЛАТЬ)" size="small" style={{ marginBottom: 16 }}>
          <Form.Item label="Тип действия" required>
            <Radio.Group value={actionType} onChange={(e) => setActionType(e.target.value)}>
              <Space direction="vertical">
                <Radio value="ai">AI генерация (RAG + LLM)</Radio>
                <Radio value="template">Использовать шаблон</Radio>
                <Radio value="manual">Ручная обработка</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Divider />

          {actionType === "ai" && (
            <div>
              <Text strong>Настройки AI генерации:</Text>

              <Form.Item
                label="AI стиль ответа"
                name={["ai_config", "style"]}
                style={{ marginTop: 16 }}
                tooltip="Стиль определяет тон и структуру ответа AI"
              >
                <Select
                  placeholder="Выберите AI стиль"
                  loading={aiStylesQuery.query.isLoading}
                  options={aiStyles.length > 0
                    ? aiStyles.map((s: any) => ({
                        value: s.style,
                        label: s.is_default ? `${s.style} (по умолчанию)` : s.style
                      }))
                    : [
                        { value: "Заботливый консультант", label: "Заботливый консультант (по умолчанию)" },
                        { value: "Проблемы с доставкой", label: "Проблемы с доставкой" },
                        { value: "Несоответствие цвета", label: "Несоответствие цвета" },
                        { value: "Аллергия и раздражение", label: "Аллергия и раздражение" },
                        { value: "Рекомендации по текстуре", label: "Рекомендации по текстуре" },
                        { value: "Рекомендации по туши", label: "Рекомендации по туши" },
                      ]
                  }
                />
              </Form.Item>

              <Form.Item
                label="Использовать базу знаний (RAG)"
                name={["ai_config", "use_rag"]}
                valuePropName="checked"
                tooltip="RAG добавляет контекст из базы знаний для более точных рекомендаций"
              >
                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
              </Form.Item>

            </div>
          )}

          {actionType === "template" && (
            <div>
              <Text strong>Выбор шаблонов для ротации:</Text>

              <Form.Item
                label="Шаблоны ответов"
                name="template_ids"
                rules={[
                  {
                    required: actionType === "template",
                    message: "Выберите хотя бы один шаблон",
                  },
                ]}
                style={{ marginTop: 16 }}
                tooltip="Выберите несколько шаблонов - система будет случайно выбирать один из них для каждого отзыва"
              >
                <Select
                  mode="multiple"
                  placeholder="Выберите шаблоны (можно несколько)"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={templates.map((template: any) => ({
                    value: template.id,
                    label: `${template.name} [${template.category || 'Без категории'}]`,
                  }))}
                  maxTagCount={3}
                  maxTagPlaceholder={(omitted) => `+${omitted.length} ещё`}
                />
              </Form.Item>

              <Alert
                message="Ротация шаблонов"
                description="При выборе нескольких шаблонов система будет случайно выбирать один из них для каждого нового отзыва. Это создаёт разнообразие в ответах."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              {templates.length === 0 && (
                <Alert
                  message="Нет активных шаблонов"
                  description="Сначала создайте шаблоны в разделе 'Шаблоны ответов'"
                  type="warning"
                  showIcon
                />
              )}
            </div>
          )}

          {actionType === "manual" && false && (
            <div>
              <Text strong>Настройки ручной обработки:</Text>

              <Form.Item
                label="Уведомлять администратора"
                name={["generation_config", "notify_slack"]}
                valuePropName="checked"
                style={{ marginTop: 16 }}
              >
                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
              </Form.Item>

              <Form.Item
                label="Назначить на"
                name={["generation_config", "assign_to"]}
              >
                <Input placeholder="email@example.com" />
              </Form.Item>
            </div>
          )}

          <Form.Item name={["generation_config", "mode"]} hidden>
            <Input />
          </Form.Item>
        </Card>

        <Card title="Предпросмотр правила" size="small" style={{ background: '#f0f7ff' }}>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <div>
              <Text strong>ЕСЛИ:</Text>
              <div style={{ marginLeft: 16, marginTop: 8 }}>
                {conditions.length === 0 ? (
                  <Text type="secondary">Любой отзыв (нет условий)</Text>
                ) : (
                  conditions.map((c, i) => {
                    // Получаем русскую метку оператора
                    const operatorLabel = operatorOptions[c.field]?.find(o => o.value === c.operator)?.label || c.operator;

                    // Получаем русскую метку значения для категорий
                    let valueLabel: string;
                    if (typeof c.value === 'boolean') {
                      valueLabel = c.value ? 'Да' : 'Нет';
                    } else if (c.field === 'stop_word_categories') {
                      valueLabel = stopWordCategoryOptions.find(o => o.value === c.value)?.label || c.value;
                    } else if (c.field === 'trigger_word_categories') {
                      valueLabel = triggerWordCategoryOptions.find(o => o.value === c.value)?.label || c.value;
                    } else if (c.field === 'sentiment') {
                      const sentimentLabels: Record<string, string> = { positive: 'Позитивный', negative: 'Негативный', neutral: 'Нейтральный' };
                      valueLabel = sentimentLabels[c.value] || c.value;
                    } else if (c.field === 'type') {
                      const typeLabels: Record<string, string> = { review: 'Отзыв', question: 'Вопрос' };
                      valueLabel = typeLabels[c.value] || c.value;
                    } else {
                      valueLabel = String(c.value);
                    }

                    return (
                      <div key={i} style={{ fontSize: 13 }}>
                        {i > 0 && <Text type="secondary">И </Text>}
                        <Tag color="blue">{fieldOptions.find(f => f.value === c.field)?.label}</Tag>
                        {' '}{operatorLabel}{' '}
                        <Tag color="green">{valueLabel}</Tag>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div>
              <Text strong>ТО:</Text>
              <div style={{ marginLeft: 16, marginTop: 8, fontSize: 13 }}>
                {actionType === "ai" && (
                  <Tag color="purple">AI генерация с RAG</Tag>
                )}
                {actionType === "template" && (
                  <Tag color="cyan">Использовать шаблон из списка</Tag>
                )}
                {actionType === "manual" && (
                  <Tag color="orange">Отправить на ручную обработку</Tag>
                )}
              </div>
            </div>
          </Space>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={formLoading}>
            Сохранить
          </Button>
        </Space>
      </Form>
    </Card>
  );
}
